import React, { FC, useState } from "react";
import { Column } from "react-table";
import {
  Container,
  Button,
  makeStyles,
  Typography,
  Checkbox,
  TextField,
} from "@material-ui/core";
import {
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import MUITable from "@material-ui/core/Table";
import FormDialog from "../../components/dialogs/FormDialog";
import Table from "../../components/Table";
import NoDataDialog from "../../components/dialogs/ErrorDialog";
import APIRequest from "../../components/helpers/APIRequest";
import * as UserAPI from "../../api/usermanagement/users";
import * as UserModels from "../../api/usermanagement/models";
import * as ShortAPI from "../../api/short/index";
import * as ShortModels from "../../api/short/models";
import moment from "moment";
import "moment/locale/da";
import { Autocomplete } from "@material-ui/lab";

moment.locale("da");

const useStyles = makeStyles((theme) => ({
  paper: {
    [theme.breakpoints.up("sm")]: {
      padding: "0",
    },
    marginTop: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  buts: {
    width: "100%",
    height: "100%",
  },
  grid: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  textHelp: {
    marginBottom: "1rem",
  },
  margTop: {
    marginTop: theme.spacing(2),
  },

  userPopup: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(2),
    overflowY: "auto",
    color: "primary",
    maxHeight: "600px",
    maxWidth: "500px",
  },

  usernameList: {
    margin: "5px",
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    borderTopRightRadius: 20,
    borderTopLeftRadius: 20,
    borderColor: "black",
    borderWidth: 2,
  },

  autoForm: {
    display: "flex",
    padding: "30px",
  },
}));

const Locations: FC = () => {
  const classes = useStyles();

  const [showNoDataDialog, setShowNoDataDialog] = React.useState(false);

  // Users
  const [users, setUsers] = useState<ShortModels.User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<ShortModels.User[]>([]);
  const [isPendingGetUsers, setPendingGetUsers] = useState(false);
  const [runGetUsers, setRunGetUsers] = useState(true);

  // Location data
  const [locations, setLocations] = useState<UserModels.UserLocations>({
    version: "",
    id: "",
    result: [],
  });
  const [isPendingLocations, setIsPendingLocations] = useState(false);
  const [runStoreAccess, setRunStoreAccess] = useState(false);
  const [isPendingStoreAccess, setPendingStoreAccess] = useState(false);

  // Location dialog
  const [locationId, setLocationId] = useState<number>(0);
  const [locationName, setLocationName] = useState("");
  const [locationUsers, setLocationUsers] = useState<Set<number>>(
    new Set<number>()
  ); // Holds the new set of users with access
  const [showDialog, setShowDialog] = useState(false);
  const [runRequestLocations, setRunRequestLocations] = useState(true);

  const columns: Column<UserModels.UserLocationData>[] = React.useMemo(
    () => [
      { Header: "Plads", id: "0", accessor: "locationName" },
      { Header: "Brugere med adgang", id: "1", accessor: "userNames" },
    ],
    []
  );

  const RequestUsers = () => {
    return (
      <APIRequest<ShortModels.User[]>
        run={runGetUsers}
        setRun={setRunGetUsers}
        pending={isPendingGetUsers}
        setIsPending={setPendingGetUsers}
        req={ShortAPI.getUsers}
        setData={setUsers}
        setShowError={setShowNoDataDialog}
      />
    );
  };

  const StoreAccess = () => {
    return (
      <APIRequest<UserModels.UserLocations>
        run={runStoreAccess}
        setRun={setRunStoreAccess}
        pending={isPendingStoreAccess}
        setIsPending={setPendingStoreAccess}
        req={() =>
          UserAPI.storeUserLocations({
            locationId: locationId,
            userAccess: Array.from(locationUsers.values()),
          })
        }
        setData={setLocations}
        setShowError={setShowNoDataDialog}
      />
    );
  };

  const RequestLocations = () => {
    return (
      <APIRequest<UserModels.UserLocations>
        run={runRequestLocations}
        setRun={setRunRequestLocations}
        pending={isPendingLocations}
        setIsPending={setIsPendingLocations}
        req={UserAPI.getUserLocations}
        setData={setLocations}
      />
    );
  };

  // Set up a row in the dialog
  const handleRowClick = (element: UserModels.UserLocationData) => {
    // Create a set of selected users
    var newSelectedUsers = new Set<number>();

    element.userIDList.forEach((id, index: any) => {
      newSelectedUsers.add(id);
      let tempElemName = element.userNamesList;
      selectedUsers[index] = { id: id, name: tempElemName[index], phone: "" };
    });

    setLocationId(element.locationId);
    setLocationName(element.locationName);
    setLocationUsers(newSelectedUsers);
    setShowDialog(true);
  };

  // Used in the dialog to update the list of locations
  const handleSelectAllUsersClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    var newSelectedUsers = new Set<number>();

    if (event.target.checked) {
      users.forEach((user) => {
        newSelectedUsers.add(user.id);
      });
    }

    setLocationUsers(newSelectedUsers);
    setSelectedUsers([]);
  };

  function updateUserValue(event: any, newUsers: any) {
    // the method that the currentlocation gets updated from the Autocomplete
    let map = new Set<number>(
      newUsers.map((itemY: any) => {
        return itemY.id;
      })
    );

    setLocationUsers(map);
    setSelectedUsers(newUsers);
  }

  return (
    <Container maxWidth={false}>
      <RequestUsers />
      <RequestLocations />
      <StoreAccess />
      <div className={classes.paper}>
        <Typography component="h1" variant="h5">
          Pladsadgang for brugere
        </Typography>
        <div className={classes.grid}>
          <Table<UserModels.UserLocationData>
            columns={columns}
            data={locations.result}
            initialState={{ pageSize: 150 }}
            clickEvent={handleRowClick}
            update={() => {
                setRunGetUsers(true)
            }}
            pending={false}
            text={""}
          />
        </div>
      </div>

      <NoDataDialog
        open={showNoDataDialog}
        onClose={() => {
          setShowNoDataDialog(false);
        }}
        title={"Intet svar"}
        text={"Noget gik galt, prøv venligst igen senere."}
      />

      <FormDialog
        title={`Vælg brugeradgang for  ${locationName}`}
        open={showDialog}
        onCloseCancel={() => setShowDialog(false)}
        onCloseSubmit={() => {
          setRunStoreAccess(true);
          setShowDialog(false);
        }}
      >
        <TableContainer style={{ minHeight: "40px" }}>
          <MUITable
            aria-labelledby="tableTitle"
            size="small"
            aria-label="enhanced table"
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      locationUsers.size > 0 &&
                      locationUsers.size < users.length
                    }
                    checked={
                      locationUsers.size > 0 &&
                      locationUsers.size === users.length
                    }
                    onChange={handleSelectAllUsersClick}
                    inputProps={{ "aria-label": "Vælg alle brugere" }}
                  />
                </TableCell>
                <TableCell align="left">Alle brugere</TableCell>
              </TableRow>
            </TableHead>
          </MUITable>
        </TableContainer>
        <div className={classes.userPopup}>
          <Autocomplete
            className={classes.autoForm}
            multiple
            limitTags={2}
            value={selectedUsers}
            options={users.sort((a, b) => (a.name > b.name ? 1 : -1))}
            getOptionLabel={(option) => option.name}
            getOptionSelected={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={selectedUsers.length === 0 ? "Brugere" : "Brugere"}
                variant="outlined"
                fullWidth
              />
            )}
            onChange={(event: any, newSelection: ShortModels.User[] | null) =>
              updateUserValue(event, newSelection)
            }
          />
          <div style={{ display: "inline-block" }}>
            Brugere med adgang:
            {selectedUsers.map((row, index) => (
              <div
                className={classes.usernameList}
                style={{ textDecorationLine: "none" }}
              >
                - {row.name} har adgang.
              </div>
            ))}
          </div>
        </div>
      </FormDialog>
    </Container>
  );
};
export default Locations;
